import { useEffect, useState } from "react";
import api from "../lib/api";
import { resolveImageUrl } from "../lib/imageUrl";
import { useApp } from "../context/AppContext";

const SECTIONS = [
  {
    key: "mill",
    en: { eyebrow: "Our Mill", title: "Where every grain begins its journey" },
    te: { eyebrow: "మా మిల్లు", title: "ప్రతి గింజ తన ప్రయాణం ప్రారంభించే చోటు" },
  },
  {
    key: "process",
    en: { eyebrow: "Milling Process", title: "Modern machines, traditional care" },
    te: { eyebrow: "మిల్లింగ్ ప్రక్రియ", title: "ఆధునిక యంత్రాలు, సాంప్రదాయక శ్రద్ధ" },
  },
  {
    key: "packing",
    en: { eyebrow: "Packing", title: "Sealed fresh, ready to ship" },
    te: { eyebrow: "ప్యాకింగ్", title: "తాజాగా సీల్ చేయబడింది, పంపడానికి సిద్ధం" },
  },
  {
    key: "quality",
    en: { eyebrow: "Quality Check", title: "Hand-inspected, never compromised" },
    te: { eyebrow: "నాణ్యత పరీక్ష", title: "చేతితో పరిశీలించబడింది" },
  },
];

export default function GalleryShowcase() {
  const { lang } = useApp();
  const [gallery, setGallery] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => setGallery({}));
  }, []);

  if (!gallery) return null;

  const visibleSections = SECTIONS.filter((s) => (gallery[s.key] || []).length > 0);
  if (visibleSections.length === 0) return null;

  return (
    <section data-testid="gallery-showcase" className="bg-[#fbf8f1] py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold mb-3">
            {lang === "en" ? "Inside our mill" : "మా మిల్లు లోపల"}
          </div>
          <h2 className={`text-3xl sm:text-4xl tracking-tight font-semibold text-stone-900 ${lang === "te" ? "font-telugu" : ""}`}>
            {lang === "en"
              ? "See where your rice is milled, packed and quality-checked"
              : "మీ బియ్యం మిల్లింగ్, ప్యాకింగ్, నాణ్యత పరీక్ష జరిగే చోటును చూడండి"}
          </h2>
        </div>

        <div className="space-y-20">
          {visibleSections.map((section, idx) => {
            const items = gallery[section.key] || [];
            const meta = lang === "en" ? section.en : section.te;
            const reverse = idx % 2 === 1;
            return (
              <div
                key={section.key}
                data-testid={`gallery-section-public-${section.key}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${
                  reverse ? "lg:[&>div:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-4">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold mb-3">
                    {meta.eyebrow}
                  </div>
                  <h3 className={`text-2xl sm:text-3xl tracking-tight font-semibold text-stone-900 ${lang === "te" ? "font-telugu" : ""}`}>
                    {meta.title}
                  </h3>
                </div>
                <div className="lg:col-span-8">
                  <ImageCollage items={items} category={section.key} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ImageCollage({ items, category }) {
  // Choose layout based on item count
  const count = items.length;

  if (count === 1) {
    return (
      <div className="aspect-[16/9] overflow-hidden rounded-sm border border-stone-200">
        <img src={resolveImageUrl(items[0].image_url)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
      </div>
    );
  }
  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {items.map((it) => (
          <div key={it.id} className="aspect-[4/3] overflow-hidden rounded-sm border border-stone-200">
            <img src={resolveImageUrl(it.image_url)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        ))}
      </div>
    );
  }
  if (count === 3) {
    return (
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2 aspect-[16/10] overflow-hidden rounded-sm border border-stone-200">
          <img src={resolveImageUrl(items[0].image_url)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
        <div className="grid grid-rows-2 gap-3">
          {items.slice(1, 3).map((it) => (
            <div key={it.id} className="aspect-[4/3] overflow-hidden rounded-sm border border-stone-200">
              <img src={resolveImageUrl(it.image_url)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  // 4+ items
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.slice(0, 8).map((it, i) => (
        <div
          key={it.id}
          className={`overflow-hidden rounded-sm border border-stone-200 ${
            i === 0 ? "sm:col-span-2 sm:row-span-2 aspect-square" : "aspect-square"
          }`}
        >
          <img src={resolveImageUrl(it.image_url)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
      ))}
    </div>
  );
}
